
import { supabase } from './supabaseClient';
import { Survey, SurveyQuestionDraft, SurveyStatus, LootItem, SurveyResponse } from '../types';
import { issueRewardCoupon } from './rewardService';

// Drop unanswered / empty values so we never persist garbage answer rows:
// undefined (untouched RANKING), null (cleared GEO/IMAGE), '' (blank text),
// [] (empty CHECKBOX), {} (untouched MATRIX).
const cleanAnswers = (answers: { questionId: string; value: any }[]) =>
  (answers || []).filter((a) => {
    const v = a.value;
    if (v === undefined || v === null || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true;
  });

export const fetchSurveyResponsesPaginated = async (surveyId: string, page: number = 1, limit: number = 10) => {
  const from = (page - 1) * limit;
  const to = from + limit - 1;

  // 1. Fetch Responses (without user join to avoid PGRST200 if FK is missing)
  const { data, error, count } = await supabase
    .from('survey_responses')
    .select(`
        *,
        answers:survey_answers(
            id,
            question_id,
            answer_value,
            question:survey_questions(question_text, question_type, config, order_index)
        )
    `, { count: 'exact' })
    .eq('survey_id', surveyId)
    .order('completed_at', { ascending: false })
    .range(from, to);

  if (error) throw error;

  // 2. Manual Join for Users
  const userIds = Array.from(new Set(data?.map((r: any) => r.user_id).filter((id: any) => id)));
  let usersMap: Record<string, any> = {};
  
  if (userIds.length > 0) {
      const { data: users } = await supabase
          .from('users')
          .select('id, email, username')
          .in('id', userIds);
      
      users?.forEach((u: any) => {
          usersMap[u.id] = u;
      });
  }

  // 3. Map Data
  const formattedData = data?.map((r: any) => ({
      ...r,
      user: usersMap[r.user_id] ? {
          email: usersMap[r.user_id].email,
          full_name: usersMap[r.user_id].username
      } : null
  }));

  return {
      data: formattedData as SurveyResponse[],
      count: count || 0
  };
};

export const fetchAllSurveyResponsesForExport = async (surveyId: string) => {
  let allData: SurveyResponse[] = [];
  let page = 0;
  const LIMIT = 1000; // Fetch in larger chunks for export
  let hasMore = true;

  while (hasMore) {
      const from = page * LIMIT;
      const to = from + LIMIT - 1;

      // 1. Fetch Responses Chunk
      const { data, error } = await supabase
        .from('survey_responses')
        .select(`
            *,
            answers:survey_answers(
                id,
                question_id,
                answer_value,
                question:survey_questions(question_text, question_type, config, order_index)
            )
        `)
        .eq('survey_id', surveyId)
        .order('completed_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      if (!data || data.length === 0) {
          hasMore = false;
          break;
      }

      // 2. Manual Join for Users (Chunk)
      const userIds = Array.from(new Set(data.map((r: any) => r.user_id).filter((id: any) => id)));
      let usersMap: Record<string, any> = {};
      
      if (userIds.length > 0) {
          const { data: users } = await supabase
              .from('users')
              .select('id, email, username')
              .in('id', userIds);
          
          users?.forEach((u: any) => {
              usersMap[u.id] = u;
          });
      }

      // 3. Map Data (Chunk)
      const formattedChunk = data.map((r: any) => ({
          ...r,
          user: usersMap[r.user_id] ? {
              email: usersMap[r.user_id].email,
              full_name: usersMap[r.user_id].username
          } : null
      }));

      allData = [...allData, ...formattedChunk];
      
      if (data.length < LIMIT) {
          hasMore = false;
      } else {
          page++;
      }
  }

  return allData;
};
export const fetchSurveyResponseById = async (responseId: string) => {
  // 1. Fetch Response Metadata
  const { data, error } = await supabase
    .from('survey_responses')
    .select(`
        *,
        survey:surveys(title, description)
    `)
    .eq('id', responseId)
    .single();

  if (error) throw error;

  // 2. Manual Join for User (only if user_id exists)
  let userData = null;
  if (data.user_id) {
      const { data: user } = await supabase
          .from('users')
          .select('email, username')
          .eq('id', data.user_id)
          .maybeSingle();
      
      if (user) {
          userData = {
              email: user.email,
              full_name: user.username
          };
      }
  }

  // 3. Fetch Answers Explicitly (Separate Query to avoid join issues)
  const { data: answers, error: answersError } = await supabase
    .from('survey_answers')
    .select(`
        id,
        question_id,
        answer_value,
        question:survey_questions(
            id,
            question_text,
            question_type,
            config,
            order_index
        )
    `)
    .eq('response_id', responseId);

  if (answersError) {
      console.error("Error fetching answers:", answersError);
  }

  // 4. Combine Data
  const formatted = {
      ...data,
      user: userData,
      answers: answers || []
  };

  return formatted as SurveyResponse & { survey: Survey };
};

export const fetchSurveys = async () => {
  const { data, error } = await supabase
    .from('surveys')
    .select(`
        *,
        digital_reward:digital_rewards(*),
        loot_table:loot_tables(*)
    `)
    .order('created_at', { ascending: false });

  if (error) throw error;
  
  return data as Survey[];
};

export const fetchSurveysPaginated = async (page: number = 1, limit: number = 10) => {
  const from = (page - 1) * limit;
  const to = from + limit - 1;

  const { data, error, count } = await supabase
    .from('surveys')
    .select('id, title, status', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(from, to);

  if (error) throw error;
  
  return { 
      data: data as Survey[], 
      count: count || 0 
  };
};

export const fetchActiveSurveys = async () => {
  const { data, error } = await supabase
    .from('surveys')
    .select('*, digital_reward:digital_rewards(*), loot_table:loot_tables(*)')
    .eq('status', 'ACTIVE')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Survey[];
};

export const fetchUserCompletedSurveyIds = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('survey_responses')
    .select('survey_id')
    .eq('user_id', user.id);

  if (error) {
    console.error("Error fetching user responses:", error);
    return [];
  }
  return data.map(r => r.survey_id);
};

// --- KIOSK ACCESS CONTROL ---
export const verifyKioskCode = async (
  surveyId: string,
  code: string,
): Promise<{ required: boolean; ok: boolean }> => {
  const { data, error } = await supabase.rpc('verify_kiosk_code', { p_survey_id: surveyId, p_code: code });
  if (error) throw error;
  return data as { required: boolean; ok: boolean };
};

// Set (or clear, with empty string) the kiosk access code. Creator-only (server-enforced).
export const setKioskCode = async (surveyId: string, code: string): Promise<void> => {
  const { error } = await supabase.rpc('set_kiosk_code', { p_survey_id: surveyId, p_code: code });
  if (error) throw error;
};

// Gated read for kiosk surveys: questions aren't directly readable (RLS), so a kiosk
// device fetches them through this code-checked RPC. Throws KIOSK_LOCKED on bad code.
export const fetchKioskQuestions = async (
  surveyId: string,
  code: string,
): Promise<SurveyQuestionDraft[]> => {
  const { data, error } = await supabase.rpc('get_kiosk_questions', { p_survey_id: surveyId, p_code: code });
  if (error) throw error;
  return (data || []) as SurveyQuestionDraft[];
};

export const fetchSurveyById = async (id: string) => {
  const { data, error } = await supabase
    .from('surveys')
    .select('*, digital_reward:digital_rewards(*), loot_table:loot_tables(*)')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Survey;
};

export const getSurveyResponseCount = async (surveyId: string) => {
  const { count, error } = await supabase
    .from('survey_responses')
    .select('*', { count: 'exact', head: true })
    .eq('survey_id', surveyId);

  if (error) throw error;
  return count || 0;
};

export const fetchSurveyQuestions = async (surveyId: string) => {
  const { data, error } = await supabase
    .from('survey_questions')
    .select('*')
    .eq('survey_id', surveyId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  return data as SurveyQuestionDraft[];
};

export const createSurvey = async (
  surveyData: Omit<Survey, 'id' | 'created_at' | 'creator_id' | 'digital_reward' | 'loot_table'>,
  questions: SurveyQuestionDraft[]
) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  // Map generic ID to specific FK column based on type
  const digitalId = surveyData.reward_type === 'DIGITAL' ? surveyData.digital_reward_id : null;
  const lootId = surveyData.reward_type === 'PHYSICAL' ? surveyData.loot_table_id : null;

  const { data: surveyResult, error: surveyError } = await supabase
    .from('surveys')
    .insert({
      title: surveyData.title,
      description: surveyData.description,
      status: surveyData.status,
      reward_type: surveyData.reward_type,
      digital_reward_id: digitalId, 
      loot_table_id: lootId,     
      max_responses: surveyData.max_responses,
      is_kiosk: surveyData.is_kiosk,
      is_live: surveyData.is_live ?? false,
      completion_box: surveyData.completion_box ?? false,
      require_login: surveyData.require_login, // New Field
      requested_attribute_ids: surveyData.requested_attribute_ids ?? null,
      data_reward_points: surveyData.data_reward_points ?? 0,
      completion_reward_points: surveyData.completion_reward_points ?? 1,
      target_attributes: surveyData.target_attributes ?? null,
      source: surveyData.source ?? 'NATIVE',
      creator_id: user.id
    })
    .select()
    .single();

  if (surveyError) throw surveyError;
  const surveyId = surveyResult.id;

  if (questions.length > 0) {
    const formattedQuestions = questions.map((q, index) => ({
      survey_id: surveyId,
      order_index: index + 1,
      question_type: q.question_type,
      question_text: q.question_text,
      config: q.config,
      gemini_validation_prompt: q.gemini_validation_prompt,
      attribute_id: q.attribute_id ?? null
    }));

    const { error: questionsError } = await supabase
      .from('survey_questions')
      .insert(formattedQuestions);

    if (questionsError) {
      console.error("Failed to insert questions", questionsError);
      await supabase.from('surveys').delete().eq('id', surveyId);
      throw questionsError;
    }
  }

  return surveyResult;
};

export const updateSurvey = async (
  id: string,
  surveyData: Partial<Survey>,
  questions: SurveyQuestionDraft[]
) => {
  // Map generic ID to specific FK column based on type
  const digitalId = surveyData.reward_type === 'DIGITAL' ? surveyData.digital_reward_id : null;
  const lootId = surveyData.reward_type === 'PHYSICAL' ? surveyData.loot_table_id : null;

  const { error: surveyError } = await supabase
    .from('surveys')
    .update({
      title: surveyData.title,
      description: surveyData.description,
      status: surveyData.status,
      reward_type: surveyData.reward_type,
      digital_reward_id: digitalId,
      loot_table_id: lootId,
      max_responses: surveyData.max_responses,
      is_kiosk: surveyData.is_kiosk,
      is_live: surveyData.is_live ?? false,
      completion_box: surveyData.completion_box ?? false,
      require_login: surveyData.require_login, // New Field
      requested_attribute_ids: surveyData.requested_attribute_ids ?? null,
      data_reward_points: surveyData.data_reward_points ?? 0,
      completion_reward_points: surveyData.completion_reward_points ?? 1,
      target_attributes: surveyData.target_attributes ?? null,
      updated_at: new Date().toISOString()
    })
    .eq('id', id);

  if (surveyError) throw surveyError;

  const { data: existingQuestions } = await supabase
    .from('survey_questions')
    .select('id')
    .eq('survey_id', id);
  
  const existingIds = existingQuestions?.map(q => q.id) || [];
  const incomingIds = questions.filter(q => !q.id.startsWith('temp-')).map(q => q.id);
  
  const idsToDelete = existingIds.filter(id => !incomingIds.includes(id));
  if (idsToDelete.length > 0) {
    await supabase.from('survey_questions').delete().in('id', idsToDelete);
  }

  // Assign order_index from the question's position in the FULL list (not within the
  // update/insert subsets) — otherwise a newly-inserted question and an existing one
  // both get order_index 1, colliding and breaking ordering + skip logic.
  const ordered = questions.map((q, index) => ({ q, order_index: index + 1 }));

  const questionsToUpdate = ordered
    .filter(({ q }) => !q.id.startsWith('temp-'))
    .map(({ q, order_index }) => ({
      id: q.id,
      survey_id: id,
      order_index,
      question_type: q.question_type,
      question_text: q.question_text,
      config: q.config,
      gemini_validation_prompt: q.gemini_validation_prompt,
      attribute_id: q.attribute_id ?? null
    }));

  const questionsToInsert = ordered
    .filter(({ q }) => q.id.startsWith('temp-'))
    .map(({ q, order_index }) => ({
      survey_id: id,
      order_index,
      question_type: q.question_type,
      question_text: q.question_text,
      config: q.config,
      gemini_validation_prompt: q.gemini_validation_prompt,
      attribute_id: q.attribute_id ?? null
    }));

  if (questionsToUpdate.length > 0) await supabase.from('survey_questions').upsert(questionsToUpdate);
  if (questionsToInsert.length > 0) await supabase.from('survey_questions').insert(questionsToInsert);
};

export const deleteSurvey = async (id: string) => {
  const { error } = await supabase
    .from('surveys')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

export const submitSurveyResponse = async (surveyId: string, answers: { questionId: string, value: any }[]) => {
  const { data: { user } } = await supabase.auth.getUser();
  
  // 1. Submit Response
  const { data: responseData, error: responseError } = await supabase
    .from('survey_responses')
    .insert({
      survey_id: surveyId,
      user_id: user ? user.id : null,
      status: 'COMPLETED',
      completed_at: new Date().toISOString()
    })
    .select()
    .single();

  if (responseError) throw responseError;

  const cleaned = cleanAnswers(answers);
  if (cleaned.length > 0) {
    const formattedAnswers = cleaned.map(a => ({
      response_id: responseData.id,
      question_id: a.questionId,
      answer_value: a.value
    }));

    const { error: answersError } = await supabase
      .from('survey_answers')
      .insert(formattedAnswers);

    if (answersError) throw answersError;
  }

  // +1 reputation point for answering (logged-in users only; idempotent server-side,
  // so re-answering can't farm points). Anonymous/kiosk respondents earn nothing here.
  if (user) {
    const { error: completionErr } = await supabase.rpc('award_survey_completion', {
      p_survey_id: surveyId,
      p_response_id: responseData.id,
    });
    if (completionErr) console.error('Completion reward failed', completionErr);
  }

  // 2. Handle Reward Issuance

  // Fetch Full Survey Config with explicitly joined tables
  const { data: survey } = await supabase
    .from('surveys')
    .select('*, digital_reward:digital_rewards(*), loot_table:loot_tables(*)')
    .eq('id', surveyId)
    .single();

  // ONLINE MODE (Authenticated User Wallet). Kiosk submissions go through
  // submitKioskResponse (code-gated, server-side reward), never this path.
  let wonItem: any = null;
  if (survey && user) {
      if (survey.completion_box && survey.loot_table) {
          // Member completion mystery box: roll the owner's loot table server-side and
          // return the prize so the runner opens the box (like kiosk). One per response.
          const { data: box, error: boxErr } = await supabase.rpc('claim_survey_box', {
            p_survey_id: surveyId,
            p_response_id: responseData.id,
          });
          if (boxErr) console.error('Survey box claim failed', boxErr);
          if (box?.ok && box.item) {
            wonItem = { name: box.item.name, image_url: box.item.image_url || null, type: box.item.type, value: box.item.value };
          }
          // A digital reward can still be issued alongside the box if configured.
          if (survey.reward_type === 'DIGITAL' && survey.digital_reward) {
            await issueRewardCoupon(survey.digital_reward.amount, survey.digital_reward.currency, 'DIGITAL_REWARD');
          }
      } else if (survey.reward_type === 'DIGITAL' && survey.digital_reward) {
          await issueRewardCoupon(survey.digital_reward.amount, survey.digital_reward.currency, 'DIGITAL_REWARD');
      } else if (survey.reward_type === 'PHYSICAL' && survey.loot_table) {
          await issueRewardCoupon(null, null, 'LOOT_BOX', survey.loot_table.id);
      }
  }

  return { ...responseData, wonItem };
};

// Gated submission for kiosk surveys. Direct inserts to kiosk responses/answers are
// blocked by RLS; this routes the whole submission through a code-checked SECURITY
// DEFINER RPC, then claims the instant reward server-side (same as the online path).
export const submitKioskResponse = async (
  surveyId: string,
  code: string,
  answers: { questionId: string; value: any }[],
) => {
  const { data, error } = await supabase.rpc('submit_kiosk_response', {
    p_survey_id: surveyId,
    p_code: code,
    p_answers: cleanAnswers(answers),
  });
  if (error) throw error;

  const responseId = (data as any)?.id;
  if (!responseId) return { id: null, wonItem: null };

  // Roll + record the physical reward (server-side, idempotent per response).
  const { data: claim, error: claimError } = await supabase.rpc('claim_kiosk_reward', {
    p_survey_id: surveyId,
    p_response_id: responseId,
  });
  if (claimError) console.error('Kiosk reward failed', claimError);
  if (claim && claim.ok && claim.item) {
    return { id: responseId, wonItem: claim.item as LootItem };
  }
  return { id: responseId, wonItem: null };
};

export const uploadSurveyFile = async (file: File) => {
  const { data: { user } } = await supabase.auth.getUser();
  
  // Use 'guest' or user ID if available to allow anonymous uploads
  const folder = user ? user.id : 'guest';

  const fileExt = file.name.split('.').pop();
  const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
  // Use explicit folder structure
  const filePath = `survey-uploads/${folder}/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('datafi') // Standardized lowercase 'datafi'
    .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
    });

  if (uploadError) {
      console.error("Storage Upload Error:", uploadError);
      throw uploadError;
  }

  const { data: { publicUrl } } = supabase.storage
    .from('datafi') // Standardized lowercase 'datafi'
    .getPublicUrl(filePath);

  return publicUrl;
};

export interface AIAnalysisResponse {
  summary: string;
  key_insights?: string[];
  sufficient_data?: boolean;
  confidence?: number | null;
  raw_engine_output?: any;
  visualization?: {
    type: 'bar' | 'line' | 'pie' | 'area';
    title: string;
    data: any[];
    dataKeys: string[];
    xAxisKey: string;
  } | null;
}

export const analyzeSurveys = async (query: string, surveyIds: string[], publicCategoryIds: number[] = [], history: any[] = []): Promise<AIAnalysisResponse> => {
  const { data, error } = await supabase.functions.invoke('analyze-surveys', {
    body: { query, surveyIds, publicCategoryIds, history }
  });

  if (error) {
    let msg = 'Analysis failed';
    try {
      const body = await (error as any).context?.json?.();
      if (body?.code === 'INSUFFICIENT_CREDITS') msg = 'Out of credits — your free monthly analyses are used up. Top up to continue.';
      else if (body?.error) msg = body.error;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return data;
};
